"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthToken } from "@/hooks/use-auth-token";
import type {
  PostRollFeedbackDecision,
  PostRollFeedbackEnvelope,
} from "@/lib/core/schemas/post-roll-feedback.schema";
import type {
  SkillCheckResult,
  SkillCheckTier,
} from "@/lib/core/schemas/skill-check.schema";
import logger from "@/lib/logger";
import { cn } from "@/lib/utils";
import { useGameState } from "./game-state-context";
import { PostRollFeedbackOffer } from "./post-roll-feedback-offer";
import { useSkillCheckAnimation } from "./skill-check-animation-context";
import "./skill-check-card.css";
import { RollLineRow } from "@/components/character/roll-line-row";
import { useCharacterSheet } from "@/hooks/use-character-sheet";
import { DEFAULT_MODIFIERS } from "./skill-check-modifiers";

type SkillCheckCardProps = {
  skillCheckId: string;
  campaignId: string;
  onResolved?: () => void;
};

type SkillCheckResolveBody = {
  flatBonus: number;
  advantage: boolean;
  disadvantage: boolean;
  bonusDice: number[];
  postRollFeedback?: PostRollFeedbackDecision;
};

// ── Helpers ──────────────────────────────────────────────────────────

function getOutcomeColor(result: SkillCheckResult): string {
  if (result.isCriticalSuccess) {
    return "text-yellow-400";
  }
  if (result.isCriticalFailure) {
    return "text-red-700";
  }
  if (result.thresholdMet !== null) {
    return "text-green-500";
  }
  return "text-red-500";
}

function getOutcomeBorderColor(result: SkillCheckResult): string {
  if (result.isCriticalSuccess) {
    return "border-yellow-400";
  }
  if (result.isCriticalFailure) {
    return "border-red-700";
  }
  if (result.thresholdMet !== null) {
    return "border-green-500";
  }
  return "border-red-500";
}

function getThresholdBadge(
  result: SkillCheckResult,
  thresholds: readonly SkillCheckTier[]
): { label: string; className: string } | null {
  if (result.thresholdMet === null) {
    return { label: "Failure", className: "bg-red-500/20 text-red-400" };
  }
  const matched = thresholds.find((t) => t.label === result.thresholdMet);
  const dc = matched?.threshold ?? "?";
  return {
    label: `${result.thresholdMet} (DC ${dc})`,
    className: result.isCriticalSuccess
      ? "bg-yellow-400/20 text-yellow-300"
      : "bg-green-500/20 text-green-400",
  };
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function formatCheckLabel({
  ability,
  proficiencyTag,
  skill,
}: {
  readonly ability?: string;
  readonly proficiencyTag?: string;
  readonly skill?: string;
}): string {
  const baseLabel = skill ?? ability;
  if (baseLabel && proficiencyTag) {
    return `${baseLabel} / ${proficiencyTag}`;
  }
  return baseLabel ?? proficiencyTag ?? "check";
}

// ── Animation hooks ──────────────────────────────────────────────────

type AnimationPhase =
  | "idle"
  | "dim"
  | "spin"
  | "cycling"
  | "landed"
  | "badge"
  | "done";

function useRollAnimation(
  result: SkillCheckResult | null,
  shouldAnimate: boolean,
  isDramatic: boolean
): {
  phase: AnimationPhase;
  displayValue: number;
  start: () => void;
} {
  const [phase, setPhase] = useState<AnimationPhase>("idle");
  const [displayValue, setDisplayValue] = useState(1);
  const rafRef = useRef<number>(0);
  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    for (const t of timeoutRefs.current) {
      clearTimeout(t);
    }
    timeoutRefs.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(() => {
    if (!result || !shouldAnimate) {
      setPhase("done");
      return;
    }

    if (prefersReducedMotion()) {
      setDisplayValue(result.roll);
      setPhase("done");
      return;
    }

    cleanup();

    const cycleDuration = isDramatic ? 400 : 150;
    const scheduleTimeout = (fn: () => void, ms: number) => {
      const id = setTimeout(fn, ms);
      timeoutRefs.current.push(id);
    };

    // Random number cycling via rAF
    const startCycling = (duration: number, onDone: () => void) => {
      const startTime = performance.now();
      const tick = (now: number) => {
        if (now - startTime >= duration) {
          setDisplayValue(result.roll);
          onDone();
          return;
        }
        setDisplayValue(Math.floor(Math.random() * 20) + 1);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    if (isDramatic) {
      // Dramatic sequence: dim -> spin -> cycle -> land -> badge -> done
      setPhase("dim");
      scheduleTimeout(() => {
        setPhase("spin");
        scheduleTimeout(() => {
          setPhase("cycling");
          startCycling(cycleDuration, () => {
            setPhase("landed");
            scheduleTimeout(() => {
              setPhase("badge");
              scheduleTimeout(() => {
                setPhase("done");
              }, 300);
            }, 400);
          });
        }, 400);
      }, 200);
    } else {
      // Brief tumble: cycle -> land -> badge -> done
      setPhase("cycling");
      startCycling(cycleDuration, () => {
        setPhase("landed");
        scheduleTimeout(() => {
          setPhase("badge");
          scheduleTimeout(() => {
            setPhase("done");
          }, 200);
        }, 250);
      });
    }
  }, [result, shouldAnimate, isDramatic, cleanup]);

  return { phase, displayValue, start };
}

// ── Component ────────────────────────────────────────────────────────

export function SkillCheckCard({
  skillCheckId,
  campaignId,
  onResolved,
}: SkillCheckCardProps) {
  const { skillChecks, errors, invalidate } = useGameState();
  const { animationCount, incrementAnimationCount } = useSkillCheckAnimation();
  const { getToken } = useAuthToken();

  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [animating, setAnimating] = useState(false);
  const [localResult, setLocalResult] = useState<SkillCheckResult | null>(null);
  const [pendingPostRollFeedback, setPendingPostRollFeedback] = useState<
    PostRollFeedbackEnvelope | null | undefined
  >(undefined);
  const animationTriggered = useRef(false);

  const check = skillChecks.get(skillCheckId);
  const error = errors.get("skill-checks");

  const { sheet } = useCharacterSheet(campaignId, check?.actorId ?? null);

  const activeResult = localResult ?? check?.result ?? null;
  const activePostRollFeedback =
    pendingPostRollFeedback !== undefined
      ? pendingPostRollFeedback
      : (check?.pendingPostRollFeedback ?? null);

  const isDramatic =
    animationCount < 2 ||
    activeResult?.isCriticalSuccess === true ||
    activeResult?.isCriticalFailure === true;

  const {
    phase,
    displayValue,
    start: startAnimation,
  } = useRollAnimation(activeResult, animating, isDramatic);

  // When animation completes, increment counter
  useEffect(() => {
    if (phase === "done" && animating) {
      setAnimating(false);
      incrementAnimationCount();
      onResolved?.();
    }
  }, [phase, animating, incrementAnimationCount, onResolved]);

  // Start animation when result becomes available after resolve
  useEffect(() => {
    if (activeResult && animating && !animationTriggered.current) {
      animationTriggered.current = true;
      startAnimation();
    }
  }, [activeResult, animating, startAnimation]);

  const handleResolve = useCallback(
    async (modifiers: SkillCheckResolveBody) => {
      setResolving(true);
      setResolveError(null);
      animationTriggered.current = false;

      try {
        const token = (await getToken()) ?? undefined;
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const res = await fetch(
          `/api/skill-checks/${skillCheckId}/resolve?campaignId=${encodeURIComponent(campaignId)}`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(modifiers),
          }
        );

        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(
            `Resolve failed: ${res.status} ${res.statusText}${body ? ` - ${body}` : ""}`
          );
        }

        const ack = await res.json();
        if (
          ack.data &&
          typeof ack.data === "object" &&
          "postRollFeedback" in (ack.data as Record<string, unknown>)
        ) {
          const data = ack.data as Record<string, unknown>;
          setPendingPostRollFeedback(
            data.postRollFeedback as PostRollFeedbackEnvelope
          );
          invalidate(["skill-checks"]);
          return;
        }

        if (ack.data) {
          setPendingPostRollFeedback(null);
          setLocalResult(ack.data as SkillCheckResult);
        }
        setAnimating(true);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to resolve skill check";
        logger.error({ err, skillCheckId }, "Failed to resolve skill check");
        setResolveError(message);
      } finally {
        setResolving(false);
      }
    },
    [skillCheckId, campaignId, getToken, invalidate]
  );

  const handleRoll = useCallback(() => {
    handleResolve(DEFAULT_MODIFIERS);
  }, [handleResolve]);

  const handlePostRollFeedback = useCallback(
    (decision: PostRollFeedbackDecision) => {
      handleResolve({ ...DEFAULT_MODIFIERS, postRollFeedback: decision });
    },
    [handleResolve]
  );

  // ── Loading state ──────────────────────────────────────────────────
  if (!check && !errors.has("skill-checks")) {
    return (
      <div className="my-2 rounded-lg border border-border bg-card p-4">
        <div className="flex animate-pulse flex-col gap-2">
          <div className="h-3 w-24 rounded bg-muted" />
          <div className="h-4 w-48 rounded bg-muted" />
          <div className="h-8 w-20 rounded bg-muted" />
        </div>
      </div>
    );
  }

  // ── Fetch error state ──────────────────────────────────────────────
  if (error) {
    return (
      <div className="my-2 rounded-lg border border-destructive/50 bg-card p-4">
        <p className="text-destructive text-sm">
          Failed to load skill check: {error}
        </p>
        <button
          className="mt-2 rounded border border-border px-3 py-1 text-xs hover:bg-accent"
          onClick={() => {
            invalidate(["skill-checks"]);
          }}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  // Narrowing guard: the loading + error guards above already cover every
  // reachable `!check` state (a check is keyed only when present; an error
  // populates `errors`, so `error = errors.get(...)` is truthy and the error
  // guard returns). This is the unreachable belt-and-braces TS needs to
  // narrow `check` to defined.
  if (!check) {
    return null;
  }

  const { skill, description, thresholds, status } = check;
  const checkLabel = formatCheckLabel({
    ability: check.ability,
    proficiencyTag: check.proficiencyTag,
    skill,
  });
  const isResolved =
    (status === "resolved" && check.result !== null) || localResult !== null;
  const sortedThresholds = [...thresholds].sort(
    (a, b) => b.threshold - a.threshold
  );
  const buttonsDisabled = resolving || animating;

  if (activePostRollFeedback) {
    return (
      <PostRollFeedbackOffer
        disabled={buttonsDisabled}
        envelope={activePostRollFeedback}
        onDecision={handlePostRollFeedback}
      />
    );
  }

  // ── Resolved state ─────────────────────────────────────────────────
  if (
    isResolved &&
    activeResult !== null &&
    phase !== "dim" &&
    phase !== "spin" &&
    phase !== "cycling"
  ) {
    const outcomeColor = getOutcomeColor(activeResult);
    const borderColor = getOutcomeBorderColor(activeResult);
    const badge = getThresholdBadge(activeResult, thresholds);
    const showGlow =
      activeResult.isCriticalSuccess && (phase === "done" || phase === "badge");
    const showVignette =
      activeResult.isCriticalFailure && (phase === "done" || phase === "badge");

    return (
      <div
        className={cn(
          "sc-border-transition my-2 rounded-lg border bg-card p-4",
          borderColor,
          showGlow && "sc-glow-pulse",
          showVignette && "sc-fail-vignette"
        )}
      >
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          {checkLabel}
        </div>
        <p className="mb-3 text-card-foreground text-sm">{description}</p>

        {/* Total */}
        <div
          className={cn(
            "sc-tabular-nums font-bold text-4xl",
            outcomeColor,
            phase === "landed" && "sc-scale-bounce-dramatic",
            phase === "badge" || phase === "done" ? "" : ""
          )}
        >
          {activeResult.total}
        </div>

        {/* Breakdown */}
        <div className="mt-1 text-muted-foreground text-sm">
          {activeResult.roll} + {activeResult.modifierTotal}
        </div>

        {/* Threshold badge */}
        {badge && (phase === "badge" || phase === "done") && (
          <div
            className={cn(
              "sc-fade-in mt-3 inline-block rounded-full px-3 py-1 font-medium text-xs",
              badge.className
            )}
          >
            {activeResult.thresholdMet !== null ? "✓" : "✗"} {badge.label}
          </div>
        )}
      </div>
    );
  }

  // ── Cancelled state ────────────────────────────────────────────────
  // LAF-105 Task 16 — when the actor that owns this check is destroyed, the
  // cancel-on-actor-destroy handler patches `profile_skill_check.status` →
  // `"cancelled"`. Render a terminal, non-interactive surface so the player
  // sees why the card stopped accepting rolls. Audit-preserving: the check
  // entity remains until LAF-221's generic GC sweep.
  if (status === "cancelled") {
    return (
      <div className="my-2 rounded-lg border border-border bg-card p-4 opacity-60">
        <div className="mb-1 flex items-center gap-2">
          <span className="inline-block rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs uppercase tracking-wide">
            Cancelled
          </span>
          <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
            {checkLabel}
          </span>
        </div>
        <p className="mb-2 text-card-foreground text-sm line-through">
          {description}
        </p>
        <p className="text-muted-foreground text-xs italic">
          This check was cancelled — the actor is no longer available.
        </p>
      </div>
    );
  }

  // ── Pending state (flat) ────────────────────────────────────────────
  // Always shows: header + description + DC summary + hydrated breakdown +
  // Roll button. No compact/expanded split, no gear toggle.
  const isDimming = phase === "dim" || phase === "spin" || phase === "cycling";

  return (
    <div
      className={cn(
        "sc-border-transition relative my-2 overflow-hidden rounded-lg border border-border bg-card p-4"
      )}
    >
      {/* Animation overlay for dramatic sequence */}
      {isDimming && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          {phase === "spin" && (
            <span
              aria-hidden="true"
              className="sc-spin text-4xl"
              role="presentation"
            >
              {"🎲"}
            </span>
          )}
          {phase === "cycling" && (
            <span
              className={cn(
                "sc-tabular-nums font-bold text-4xl text-card-foreground"
              )}
            >
              {displayValue}
            </span>
          )}
        </div>
      )}

      <div className={cn(isDimming && "sc-dim")}>
        {/* Skill label */}
        <div className="mb-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
          {checkLabel}
        </div>

        {/* Description */}
        <p className="mb-2 text-card-foreground text-sm">{description}</p>

        {/* DC threshold list (each DC + its outcome label) */}
        <div className="mb-3 flex flex-col gap-1">
          {sortedThresholds.map((t) => (
            <div
              className="text-muted-foreground text-xs"
              key={`${t.threshold}-${t.label}`}
            >
              <span className="font-medium text-card-foreground">
                DC {t.threshold}
              </span>{" "}
              {t.label}
            </div>
          ))}
        </div>

        {/* Hydrated modifier preview — always visible when sheet is loaded */}
        {check.skill &&
          sheet?.skills &&
          Array.isArray(sheet.skills[check.skill]) && (
            <div className="mb-3 rounded border border-border/50 bg-muted/30 p-2">
              <div className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wider">
                Hydrated modifiers (already applied)
              </div>
              <RollLineRow
                label={check.skill}
                mods={sheet.skills[check.skill]}
              />
            </div>
          )}

        {/* Resolve error */}
        {resolveError && (
          <div className="mb-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive text-xs">
            {resolveError}
          </div>
        )}

        {/* Roll button */}
        <button
          className="rounded bg-primary px-4 py-1.5 font-medium text-primary-foreground text-sm hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          disabled={buttonsDisabled}
          onClick={handleRoll}
          type="button"
        >
          {resolving ? "Rolling..." : "Roll"}
        </button>
      </div>
    </div>
  );
}
